import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bidderApi, CreateBidderDto, UpdateBidderDto } from '../../../api/bidders';
import { notifications } from '@mantine/notifications';

// Hook to get bidders for a tender
export function useBidders(tenderId: string) {
  return useQuery({
    queryKey: ['bidders', tenderId],
    queryFn: () => bidderApi.listBidders(tenderId),
    enabled: !!tenderId,
  });
}

// Hook to register a new bidder
export function useRegisterBidder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, data }: { tenderId: string; data: CreateBidderDto }) =>
      bidderApi.registerBidder(tenderId, data),
    onSuccess: (_, { tenderId }) => {
      // Invalidate and refetch bidders for this tender
      queryClient.invalidateQueries({ queryKey: ['bidders', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Bidder registered successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error registering bidder:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to register bidder. Please try again.',
        color: 'red',
      });
    },
  });
}

// Hook to update a bidder
export function useUpdateBidder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, bidderId, data }: { tenderId: string; bidderId: string; data: UpdateBidderDto }) =>
      bidderApi.updateBidder(tenderId, bidderId, data),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['bidders', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Bidder updated successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error updating bidder:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update bidder. Please try again.',
        color: 'red',
      });
    },
  });
}

// Hook to remove a bidder
export function useRemoveBidder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, bidderId }: { tenderId: string; bidderId: string }) =>
      bidderApi.removeBidder(tenderId, bidderId),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['bidders', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Bidder removed successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error removing bidder:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to remove bidder. Please try again.',
        color: 'red',
      });
    },
  });
}

// Hook to register an existing bidder
export function useRegisterExistingBidder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenderId, existingBidderId }: { tenderId: string; existingBidderId: string }) =>
      bidderApi.registerExistingBidder(tenderId, existingBidderId),
    onSuccess: (_, { tenderId }) => {
      queryClient.invalidateQueries({ queryKey: ['bidders', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Existing bidder registered successfully',
        color: 'green',
      });
    },
    onError: (error) => {
      console.error('Error registering existing bidder:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to register existing bidder. Please try again.',
        color: 'red',
      });
    },
  });
}

// Hook to search existing bidders
export function useSearchExistingBidders(query: string, enabled: boolean = false) {
  return useQuery({
    queryKey: ['search-bidders', query],
    queryFn: () => bidderApi.searchExistingBidders(query),
    enabled: enabled && !!query.trim(),
  });
}

// Hook to get bidder details
export function useBidderDetails(bidderId: string) {
  return useQuery({
    queryKey: ['bidder-details', bidderId],
    queryFn: () => bidderApi.getBidderDetails(bidderId),
    enabled: !!bidderId,
  });
}

// Hook to get bidder tender history
export function useBidderTenderHistory(bidderId: string) {
  return useQuery({
    queryKey: ['bidder-history', bidderId],
    queryFn: () => bidderApi.getBidderTenderHistory(bidderId),
    enabled: !!bidderId,
  });
}

// Hook to update bidder qualification status
export function useUpdateBidderQualification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      tenderId, 
      bidderId, 
      qualificationStatus, 
      qualificationNotes 
    }: { 
      tenderId: string; 
      bidderId: string; 
      qualificationStatus: 'QUALIFIED' | 'DISQUALIFIED';
      qualificationNotes?: string;
    }) =>
      bidderApi.updateBidder(tenderId, bidderId, {
        qualificationStatus,
        qualificationNotes: qualificationNotes || undefined,
      }),
    onSuccess: (_, { tenderId }) => {
      // Invalidate bid submissions to refresh qualification status
      queryClient.invalidateQueries({ queryKey: ['bid-submissions', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['bidders', tenderId] });
      queryClient.invalidateQueries({ queryKey: ['tender', tenderId] });
      
      notifications.show({
        title: 'Success',
        message: 'Bidder qualification status updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      console.error('Error updating bidder qualification:', error);
      const errorMessage = error?.response?.data?.error || 'Failed to update qualification status. Please try again.';
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    },
  });
}
